import { render } from "preact";
import { useState, useEffect, useRef } from "preact/hooks";
import { modal, picModal } from "../../ui/modal";
import { setText } from "../../utils/dom";
import { api } from "../../api";

interface ItopReseller {
  id: number;
  name: string;
}
interface ItopArtist {
  id: number;
  username: string;
  name: string;
  profilePicUrl: string;
}
interface ItopCollection {
  id: string;
  name: string;
  thumbUrl: string;
  url: string;
}
interface ItopArtwork {
  id: number;
  title: string;
  photographer: string;
  imgUrl: string;
}
interface ItopSearchResponse {
  isEmpty: boolean;
  isAdmin: boolean;
  resellers: ItopReseller[];
  artists: ItopArtist[];
  collections: ItopCollection[];
  artworks: ItopArtwork[];
}

function ResellerItem({ r }: { r: ItopReseller }) {
  return (
    <div
      class="itopsearch-results-item-compressed"
      style="cursor: pointer;"
      onClick={() => modal(`partnerdetails:${r.id}:`, "large")}
    >
      {r.name}
    </div>
  );
}

function ArtistItem({ a, isAdmin }: { a: ItopArtist; isAdmin: boolean }) {
  const handleClick = () => {
    if (isAdmin) {
      modal(`artistdetails:${a.id}:`, "large");
    } else {
      location.href = `/${a.username}`;
    }
  };

  return (
    <div class="itopsearch-results-item" style="cursor: pointer;" onClick={handleClick}>
      <div class="itopsearch-results-item-img">
        <img src={a.profilePicUrl} />
      </div>
      <div class="itopsearch-results-item-header">{a.name}</div>
    </div>
  );
}

function CollectionItem({ c }: { c: ItopCollection }) {
  return (
    <div
      class="itopsearch-results-item"
      style="cursor: pointer;"
      onClick={() => {
        location.href = `/collection/${c.url}`;
      }}
    >
      <div class="itopsearch-results-item-img">
        <img src={c.thumbUrl} />
      </div>
      <div class="itopsearch-results-item-header">{c.name}</div>
    </div>
  );
}

function ArtworkItem({ w }: { w: ItopArtwork }) {
  return (
    <div
      class="itopsearch-results-item"
      style="cursor: pointer;"
      onClick={(e) => picModal(`view:${w.id}`, "full", String(w.id), e)}
    >
      <div class="itopsearch-results-item-img">
        <img src={w.imgUrl} />
      </div>
      <div class="itopsearch-results-item-header">{w.title}</div>
    </div>
  );
}

function EmptyResults() {
  return (
    <div class="itopsearch-noresults">
      <div class="itopsearch-noresults-header">
        <i class="far fa-frown" />
      </div>
      <div class="itopsearch-noresults-subheader">No artworks, artists or collections found</div>
    </div>
  );
}

function LoadingResults({ query }: { query: string }) {
  return (
    <div class="itopsearch-noresults">
      <div class="itopsearch-noresults-header">
        <div class="loader">
          <img src="https://static.pictufy.com/assets/img/pictufy-icon-black.png" />
        </div>
      </div>
      <div class="itopsearch-noresults-subheader">Searching for "{query}"</div>
    </div>
  );
}

function SearchResults({ data }: { data: ItopSearchResponse }) {
  if (data.isEmpty) return <EmptyResults />;

  return (
    <>
      {data.resellers.length > 0 && (
        <>
          <div class="itopsearch-catheader">Resellers</div>
          <div class="itopsearch-results-items">
            {data.resellers.map((r) => (
              <ResellerItem key={r.id} r={r} />
            ))}
          </div>
        </>
      )}
      {data.artists.length > 0 && (
        <>
          <div class="itopsearch-catheader">Artists</div>
          {data.artists.map((a) => (
            <ArtistItem key={a.id} a={a} isAdmin={data.isAdmin} />
          ))}
        </>
      )}
      {data.collections.length > 0 && (
        <>
          <div class="itopsearch-catheader">Collections</div>
          {data.collections.map((c) => (
            <CollectionItem key={c.id} c={c} />
          ))}
        </>
      )}
      {data.artworks.length > 0 && (
        <>
          <div class="itopsearch-catheader">Artworks</div>
          {data.artworks.map((w) => (
            <ArtworkItem key={w.id} w={w} />
          ))}
        </>
      )}
    </>
  );
}

function TopSearchDropdown({ query }: { query: string }) {
  const [results, setResults] = useState<ItopSearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setLoading(true);
    setResults(null);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const timer = setTimeout(() => {
      // tRPC has no AbortSignal; reuse the controller purely as a "is this effect
      // still current?" flag so a stale response can't overwrite a newer query.
      api.search
        .itopSearch({ q: query })
        .then((data) => {
          if (controller.signal.aborted) return;
          setResults(data);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          console.error(err);
          setLoading(false);
        });
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  if (loading) return <LoadingResults query={query} />;
  if (results) return <SearchResults data={results} />;
  return null;
}

let currentQuery = "";

function showDropdown(): void {
  const targetEl = document.getElementById("itopsearch-target");
  const naviEl = document.getElementById("new-navi");
  const mainEl = document.getElementById("main");
  if (targetEl) targetEl.style.display = "block";
  if (naviEl) naviEl.classList.add("navi-fixed");
  if (mainEl) mainEl.classList.add("main-navi-fixed");
}

function hideDropdown(): void {
  const targetEl = document.getElementById("itopsearch-target");
  const naviEl = document.getElementById("new-navi");
  const mainEl = document.getElementById("main");
  if (targetEl) targetEl.style.display = "none";
  if (naviEl) naviEl.classList.remove("navi-fixed");
  if (mainEl) mainEl.classList.remove("main-navi-fixed");
  currentQuery = "";
}

/**
 * Called from PHP onkeyup on the search input.
 * Replaces the old itopsearch() + itopsearchdo() flow.
 */
export function mountTopSearchDropdown(q: string): void {
  if (q === currentQuery) return;

  const container = document.getElementById("itopsearch-results");
  const headerEl = document.getElementById("itopsearch-header");
  if (!container) return;

  if (q.length > 2) {
    currentQuery = q;
    showDropdown();
    // setText, not setHtml: `q` is whatever the user typed, and innerHTML would
    // parse it — `<img src=x onerror=…>` in the search box executed. The clear on
    // the other branch already used setText.
    if (headerEl) setText(headerEl, `Searching for ${q}`);
    render(<TopSearchDropdown query={q} />, container);
  } else {
    currentQuery = "";
    hideDropdown();
    if (headerEl) setText(headerEl, "");
    render(null, container);
  }
}

/**
 * Hide dropdown — called from checkitopsearch / external code.
 */
export function unmountTopSearchDropdown(): void {
  hideDropdown();
  const container = document.getElementById("itopsearch-results");
  if (container) render(null, container);
}
